import React from "react";
import { useTranslation } from "react-i18next";
import { allowsResidential, parseNumber } from "../../utils/propertyRequestForm";
import { amount, area, categoryLabel, cityLabel, delegationLabel, rangeLabel } from "./labels";

// Readable summary of a request (form state or API object). Contact details are never displayed.
export default function PropertyRequestSummary({ criteria, text, contactPreference }) {
  const { t } = useTranslation();
  const value = (v) => { const n = parseNumber(v); return n == null || Number.isNaN(n) ? null : n; };
  const none = t("propertyRequest.summary.none");
  const residential = allowsResidential(criteria);
  const location = criteria.city
    ? (criteria.delegations.length
      ? `${criteria.delegations.map((d) => delegationLabel(t, criteria.city, d)).join(" · ")} — ${cityLabel(t, criteria.city)}`
      : t("propertyRequest.summary.wholeCity", { city: cityLabel(t, criteria.city) }))
    : none;
  const rows = [
    ["transaction", criteria.transaction ? t(`propertyRequest.transaction.${criteria.transaction}`) : none],
    ["categories", criteria.categories.length ? criteria.categories.map((c) => categoryLabel(t, c, criteria.transaction)).join(" · ") : none],
    ["location", location],
    ["budget", rangeLabel(t, value(criteria.budgetMin), value(criteria.budgetMax), amount(t, criteria.transaction))],
    ["surface", rangeLabel(t, value(criteria.surfaceMin), value(criteria.surfaceMax), area)],
    ...(residential ? [
      ["bedrooms", value(criteria.bedroomsMin) != null ? t("propertyRequest.summary.bedroomsValue", { count: value(criteria.bedroomsMin) }) : none],
      ["attributes", criteria.attributes.length ? criteria.attributes.map((a) => t(`propertyRequest.attribute.${a}`)).join(" · ") : none],
    ] : []),
    ...(contactPreference ? [["contact", t(`propertyRequest.contact.${contactPreference}`)]] : []),
  ];
  return (
    <div className="space-y-4">
      {text?.trim() && (
        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("propertyRequest.summary.text")}</h2>
          <p className="mt-2 whitespace-pre-line break-words text-base text-slate-900">{text.trim()}</p>
        </section>
      )}
      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <h2 className="border-b border-slate-100 px-4 py-3 text-lg font-bold text-slate-900 sm:px-6">{t("propertyRequest.preview.summary")}</h2>
        <dl className="divide-y divide-slate-100">
          {rows.map(([key, label]) => (
            <div key={key} className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-summary={key}>
              <dt className="text-sm text-slate-500">{t(`propertyRequest.summary.${key}`)}</dt>
              <dd className="break-words text-base font-semibold text-slate-900 sm:col-span-2">{label}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}
